import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";

const GRI403_8 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    OHSManagementSystem: {
      OHSManagementSystemAlignmentDetails: "",
      OHSSystemCoverageDetails: "",
      OHSSystemInternalAuditCoverage: "",
      WorkerExclusionReasons: "",
    },

    excludedWorkers: "",
    contextualInformation: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);

        const data =
          response.data.data.GRI400?.workersCoveredOccupationalHealth || {};
        setFormData({
          OHSManagementSystem: {
            OHSManagementSystemAlignmentDetails:
              data.OHSManagementSystem?.OHSManagementSystemAlignmentDetails ||
              "",
            OHSSystemInternalAuditCoverage:
              data.OHSManagementSystem?.OHSSystemInternalAuditCoverage || "",
            OHSSystemCoverageDetails:
              data.OHSManagementSystem?.OHSSystemCoverageDetails || "",
            WorkerExclusionReasons:
              data.OHSManagementSystem?.WorkerExclusionReasons || "",
          },
          excludedWorkers: data.excludedWorkers || "",
          contextualInformation: data.contextualInformation || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.OHSManagementSystem.OHSManagementSystemAlignmentDetails) {
      newErrors.OHSManagementSystemAlignmentDetails = "This field is required";
      formIsValid = false;
    }

    if (!formData.OHSManagementSystem.OHSSystemCoverageDetails) {
      newErrors.OHSSystemCoverageDetails = "This field is required";
      formIsValid = false;
    }

    if (!formData.OHSManagementSystem.OHSSystemInternalAuditCoverage) {
      newErrors.OHSSystemInternalAuditCoverage = "This field is required";
      formIsValid = false;
    }

    if (!formData.OHSManagementSystem.WorkerExclusionReasons) {
      newErrors.WorkerExclusionReasons = "This field is required";
      formIsValid = false;
    }

    if (!formData.excludedWorkers) {
      newErrors.excludedWorkers = "This field is required";
      formIsValid = false;
    }

    if (!formData.contextualInformation) {
      newErrors.contextualInformation = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;  // Validate form before submission
      const payload = {
        type: "GRI400",
        submitted403_8: true,
        workersCoveredOccupationalHealth: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/403-9/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/403-9/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && (
        <p className={styles.section_heading}>
          403 : Occupational Health and Safety
        </p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        {" "}
        403-8 : Workers covered by an occupational health and safety management
        system
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <SectionHeading text="a. Mention if the organization has established an occupational health and safety management system in alignment with legal requirements and/or recognized standards/guidelines:" />
        <CustomTextField
          value={
            formData.OHSManagementSystem.OHSManagementSystemAlignmentDetails
          }
          onChange={(e) =>
            handleNestedInputChange(
              "OHSManagementSystem",
              "OHSManagementSystemAlignmentDetails",
              e.target.value
            )
          }
          error={errors.OHSManagementSystemAlignmentDetails}
          helperText={errors.OHSManagementSystemAlignmentDetails}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. What is the total number and percentage of individuals, including employees and non-employee workers whose work and/or workplace is under the organization's control, covered by such a system?" />
          <CustomTextField
            value={formData.OHSManagementSystem.OHSSystemCoverageDetails}
            onChange={(e) =>
              handleNestedInputChange(
                "OHSManagementSystem",
                "OHSSystemCoverageDetails",
                e.target.value
              )
            }
            error={errors.OHSSystemCoverageDetails}
            helperText={errors.OHSSystemCoverageDetails}
            disabled={disabled}
          />

          <SectionHeading text="ii. Among the total number mentioned in (i), what is the number and percentage covered by an internally audited occupational health and safety management system?" />
          <CustomTextField
            value={formData.OHSManagementSystem.OHSSystemInternalAuditCoverage}
            onChange={(e) =>
              handleNestedInputChange(
                "OHSManagementSystem",
                "OHSSystemInternalAuditCoverage",
                e.target.value
              )
            }
            error={errors.OHSSystemInternalAuditCoverage}
            helperText={errors.OHSSystemInternalAuditCoverage}
            disabled={disabled}
          />

          <SectionHeading text="iii. Among the total number mentioned in (i), what is the number and percentage covered by an externally audited or certified occupational health and safety management system? " />
          <CustomTextField
            value={formData.OHSManagementSystem.WorkerExclusionReasons}
            onChange={(e) =>
              handleNestedInputChange(
                "OHSManagementSystem",
                "WorkerExclusionReasons",
                e.target.value
              )
            }
            error={errors.WorkerExclusionReasons}
            helperText={errors.WorkerExclusionReasons}
            disabled={disabled}
          />
        </Box>

        <SectionHeading
          text="b. Explain whether any workers have been excluded from the disclosed data, and if so, provide the reasons for their exclusion? Additionally, please specify the types of workers excluded."
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.excludedWorkers}
          onChange={(e) => handleInputChange("excludedWorkers", e.target.value)}
          error={errors.excludedWorkers}
          helperText={errors.excludedWorkers}
          disabled={disabled}
        />

        <SectionHeading
          text="c. Provide any contextual information necessary to comprehend how the data were compiled. This may include details on the standards, methodologies, and assumptions utilized in the data collection process."
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.contextualInformation}
          onChange={(e) =>
            handleInputChange("contextualInformation", e.target.value)
          }
          error={errors.contextualInformation}
          helperText={errors.contextualInformation}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI403_8;
